import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import MobileNav from '@/components/layout/MobileNav';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Loader2, Sparkles, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { API_BASE } from '@/lib/api';
import AlbumCover from '@/components/albums/AlbumCover';
import PhotoModal from '@/components/albums/PhotoModal';

const API = API_BASE;

export default function SmartCollectionPage() {
  const { theme } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(theme || '');
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [denied, setDenied] = useState(false);

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/smart-collections/${encodeURIComponent(decoded)}`,
        { withCredentials: true },
      );
      setPhotos(data.photos || []);
    } catch (err) {
      if (err.response?.status === 402) setDenied(true);
    } finally {
      setLoading(false);
    }
  }, [decoded]);

  useEffect(() => { fetch(); }, [fetch]);

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-20 md:pb-8" data-testid="smart-collection-page">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/albums')}
            className="rounded-full"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-stone-900">{decoded}</h1>
            <p className="text-sm text-stone-500">
              {loading ? 'Loading…' : `${photos.length} photo${photos.length === 1 ? '' : 's'} grouped by AI`}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 text-teal-600 animate-spin mx-auto mb-4" />
          </div>
        ) : denied ? (
          <div className="card-soft text-center py-12">
            <Sparkles className="w-12 h-12 mx-auto text-teal-500 mb-4" />
            <h2 className="text-xl font-semibold text-stone-900 mb-2">Premium feature</h2>
            <p className="text-stone-500 mb-6">
              Smart Collections are part of the Family and Premium plans.
            </p>
            <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white rounded-full">
              <a href="/pricing">See plans</a>
            </Button>
          </div>
        ) : photos.length === 0 ? (
          <div className="card-soft text-center py-12">
            <ImageIcon className="w-12 h-12 mx-auto text-stone-300 mb-4" />
            <p className="text-stone-500">No photos in this collection yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {photos.map((p, i) => (
              <button
                type="button"
                key={p.file_id}
                onClick={() => setActiveId(p.file_id)}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-stone-100 text-left"
                data-testid={`collection-photo-${i}`}
              >
                <AlbumCover fileId={p.file_id} />
                {p.ai_tags?.caption && (
                  <div className="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <p className="text-white text-xs line-clamp-2">{p.ai_tags.caption}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </main>
      <MobileNav />
      <PhotoModal
        fileId={activeId}
        open={!!activeId}
        onOpenChange={(o) => { if (!o) setActiveId(null); }}
      />
    </div>
  );
}
